import type { ServiceManager } from '@/framework/services/service-manager';
import { setLayerRuntimeValue } from '@/utils/layer-kit';
import { getPlainTextFontByPretext } from '@/utils/text-kit';
import type { ILayerProps, IMarkupLayerProps } from '@/version-types';
import { getMarkupLabelRenderText } from './utils/label-layout';

type MarkupFontServices = Pick<ServiceManager, 'font'>;

/**
 * 尽力把 Markup 字体子集注册到 document；失败时由原字体和系统字体继续渲染。
 */
export const prepareMarkupLayerDocumentFonts = async (
    services: MarkupFontServices,
    layer: ILayerProps,
): Promise<boolean> => {
    const { label } = layer.data as IMarkupLayerProps;
    const text = getMarkupLabelRenderText(label);
    if (!text) return true;

    let subsetFamily: string | null = null;
    try {
        subsetFamily = await services.font.loadSubset(label.fontFamily, text, {
            registerTo: 'document',
        });
    } catch {
        return false;
    }
    if (!subsetFamily || typeof document === 'undefined' || !document.fonts) return false;

    setLayerRuntimeValue(layer, {
        fontFamilyMap: {
            ...(layer.__runtime?.fontFamilyMap || {}),
            [label.fontFamily]: subsetFamily,
        },
    });

    try {
        const font = getPlainTextFontByPretext({
            fontFamily: subsetFamily,
            fontSize: label.fontSize,
            bold: label.bold,
            italic: label.italic,
        });
        const fontFaces = await document.fonts.load(font, text);
        return fontFaces.length > 0;
    } catch {
        return false;
    }
};
